import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import { getLanguages } from "@/api/languages/languages-services";
import { Checkbox } from "@/components/ui/checkbox";
import {
  getForms,
  getQuestionsByForm,
  getConfigurableTexts,
} from "@/api/forms-questions/forms-questions-services";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Loader from "@/components/utils/loader";
import { useStore } from "@/store";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import {
  buildLegalTextOptions,
  getUpdateQuestionDefaultValues,
  normalizeBooleanField,
  updateQuestionPayload,
} from "./question-form-utils";

export const formSchema = z
  .object({
    texts: z.record(
      z.string(),
      z.object({
        text: z.string().min(1, { message: "Este campo es requerido" }),
        id: z.number().nullable().optional(),
      })
    ),
    descriptions: z.record(
      z.string(),
      z.object({
        text: z.string().optional(),
        id: z.number().nullable().optional(),
      })
    ),
    placeholders: z.record(
      z.string(),
      z.object({
        text: z.string().optional(),
        id: z.number().nullable().optional(),
      })
    ),
    screen: z
      .number()
      .min(1, { message: "Screen is required" })
      .nullable()
      .optional(),
    type: z.string().min(1, { message: "Este campo es requerido" }),
    legalTextKey: z.string().optional(),
    optionsText: z
      .record(
        z.string(),
        z.array(
          z.object({
            text: z.string().optional(),
            id: z.number().nullable().optional(),
            optionId: z.number().nullable().optional(),
          })
        )
      )
      .optional(),
    active: z.boolean().optional(),
    required: z.boolean().optional(),
    anonymizable: z.boolean().optional(),
    depends_id: z.preprocess((value) => {
      if (value === "" || value == null) return null;
      if (typeof value === "string") return Number(value);
      if (typeof value === "object") {
        if (value && typeof value.id !== "undefined") return Number(value.id);
        if (value && typeof value.value !== "undefined") return Number(value.value);
      }
      return value;
    }, z.number().nullable().optional()),
    related_question: z.preprocess((value) => {
      if (value === "" || value == null) return null;
      if (typeof value === "string") return Number(value);
      if (typeof value === "object") {
        if (value && typeof value.id !== "undefined") return Number(value.id);
        if (value && typeof value.value !== "undefined") return Number(value.value);
      }
      return value;
    }, z.number().nullable().optional()),
  })
  .superRefine((values, ctx) => {
    if (values.related_question && !values.depends_id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Este campo es requerido",
        path: ["depends_id"],
      });
    }
  });

const UpdateQuestion = ({ formType, closeModal }) => {
  const { t, i18n } = useTranslation();

  const [questionLanguages, setQuestionLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);

  const [selectedQuestion, setSelectedQuestion] = useState(null);

  const [allQuestions, setAllQuestions] = useState([]);
  const [legalTexts, setLegalTexts] = useState([]);

  const questionToUpdateData = useStore((state) => state.questionToUpdateData);
  const UpdatetQuestion = useStore((state) => state.UpdatetQuestion);
  const [loading, setLoading] = useState(false);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      texts: {},
      descriptions: {},
      placeholders: {},
      screen: null,
      type: "",
      optionsText: {},
      active: false,
      required: false,
      anonymizable: false,
      depends_id: null,
      related_question: null,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: `optionsText.${selectedLanguage}`,
  });

  // Añadir este estado para guardar todos los textos de las opciones
  const [optionsReferenceTexts, setOptionsReferenceTexts] = useState({});

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const languages = await getLanguages();
        setQuestionLanguages(languages);

        const defaultLanguage = i18n.language;

        const normalizedDefaultLanguage = defaultLanguage.split("-")[0];

        const foundLanguage = languages.find(
          (lang) => lang.code === normalizedDefaultLanguage,
        );

        if (foundLanguage) {
          setSelectedLanguage(normalizedDefaultLanguage);
        } else {
          setSelectedLanguage(languages[0]?.code || "");
        }
      } catch (error) {
        console.error("Error fetching languages:", error);
      }
    };
    fetchLanguages();
  }, [i18n.language]);

  // Políticas disponibles en el tenant para asociarlas a una pregunta `terms`.
  useEffect(() => {
    const fetchLegalTexts = async () => {
      try {
        const texts = await getConfigurableTexts();
        const list = Array.isArray(texts)
          ? texts
          : Array.isArray(texts?.data)
            ? texts.data
            : [];
        setLegalTexts(list);
      } catch (error) {
        console.error("Error fetching configurable texts:", error);
      }
    };
    fetchLegalTexts();
  }, []);

  // Cargar todas las preguntas del formulario actual
  useEffect(() => {
    const fetchAllQuestions = async () => {
      try {
        const allForms = await getForms();
        const form = (allForms || []).find((f) => f.type === formType);
        if (form?.id) {
          const questions = await getQuestionsByForm(form.id);
          setAllQuestions(questions || []);
        }
      } catch (error) {
        console.error("Error fetching questions:", error);
      }
    };
    fetchAllQuestions();
  }, [formType]);

  useEffect(() => {
    const fetchQuestionToUpdate = async () => {
      if (questionToUpdateData) {
        try {
          // Guardar todos los textos de las opciones para poder usarlos como referencia después
          const allOptionsTexts = {};
          
          if (questionToUpdateData.options && questionToUpdateData.options.length > 0) {
            questionToUpdateData.options.forEach(option => {
              if (!allOptionsTexts[option.id]) {
                allOptionsTexts[option.id] = {};
              }
              
              // Guardar los textos para cada idioma
              if (option.text && Array.isArray(option.text)) {
                option.text.forEach(textItem => {
                  allOptionsTexts[option.id][textItem.language] = textItem.text;
                });
              }
            });
          }
          
          // Save reference texts for options
          setOptionsReferenceTexts(allOptionsTexts);
          
          form.reset(getUpdateQuestionDefaultValues(questionToUpdateData));

          // If depends_id exists, we look for the related question
          if (questionToUpdateData.depends && questionToUpdateData.depends.id) {
            const relatedOptionId = questionToUpdateData.depends.id;

            const foundQuestion = allQuestions.find(q =>
                q.options && Array.isArray(q.options) && q.options.some(o => o?.id === relatedOptionId)
            );


            if (foundQuestion) {
              // Set the related question
              setSelectedQuestion(foundQuestion);
              form.setValue("related_question", foundQuestion.id);
            }
          }
        } catch (error) {
          console.error("Error fetching question to update:", error);
        }
      }
    };
    fetchQuestionToUpdate();
  }, [questionToUpdateData, form, allQuestions, formType]);

  useEffect(() => {
    if (selectedLanguage) {
      const currentOptions = form.getValues(`optionsText.${selectedLanguage}`) || [];

      // Get options in other languages to suggest translations
      let optionsToDisplay = [...currentOptions];

      // If no options in the selected language, try to use other languages as reference
      if (currentOptions.length === 0) {
        const allOptionsInForm = form.getValues("optionsText") || {};
        const allLanguagesWithOptions = Object.keys(allOptionsInForm || {});

        if (allLanguagesWithOptions.length > 0) {
          // Prefer Spanish or English as reference
          let referenceLanguage = "";
          if (allLanguagesWithOptions.includes("es")) {
            referenceLanguage = "es";
          } else if (allLanguagesWithOptions.includes("en")) {
            referenceLanguage = "en";
          } else {
            referenceLanguage = allLanguagesWithOptions[0];
          }

          const referenceOptions = allOptionsInForm[referenceLanguage] || [];

          // Create empty options but keep references to optionId and reference text
          optionsToDisplay = referenceOptions.map(option => {
            if (!option) return { text: "", id: null, optionId: null, referenceText: "" };

            // Reference text from the stored option texts
            let refText = "";
            if (option.optionId && optionsReferenceTexts[option.optionId]) {
              if (optionsReferenceTexts[option.optionId]["es"]) {
                refText = optionsReferenceTexts[option.optionId]["es"];
              } else if (optionsReferenceTexts[option.optionId]["en"]) {
                refText = optionsReferenceTexts[option.optionId]["en"];
              } else {
                const availableLanguages = Object.keys(optionsReferenceTexts[option.optionId]);
                if (availableLanguages.length > 0) {
                  refText = optionsReferenceTexts[option.optionId][availableLanguages[0]];
                }
              }
            }

            return {
              text: "",
              id: null,
              optionId: option.optionId || null,
              referenceText: refText
            };
          });
        }
      }

      // Rebuild options
      remove();
      for (const option of optionsToDisplay) {
        append(option);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage, optionsReferenceTexts]);

  // Restaurar el useEffect para manejar el cambio de idioma
  useEffect(() => {
    if (questionToUpdateData && selectedLanguage) {
      const texts = questionToUpdateData.texts || [];
      const descriptions = questionToUpdateData.descriptions || [];
      const placeholders = questionToUpdateData.placeholders || [];
      const options = questionToUpdateData.options || [];

      const textData = texts.find((t) => t.language === selectedLanguage);
      form.setValue("texts", {
        ...form.getValues("texts"),
        [selectedLanguage]: {
          text: textData?.text || "",
          id: textData?.id || null
        }
      });

      const descData = descriptions.find((d) => d.language === selectedLanguage);
      form.setValue("descriptions", {
        ...form.getValues("descriptions"),
        [selectedLanguage]: {
          text: descData?.text || "",
          id: descData?.id || null
        }
      });

      const placeholderData = placeholders.find((p) => p.language === selectedLanguage);
      form.setValue("placeholders", {
        ...form.getValues("placeholders"),
        [selectedLanguage]: {
          text: placeholderData?.text || "",
          id: placeholderData?.id || null
        }
      });

      const optionsTexts = options.map((option) => {
        const optionText = option.text?.find((t) => t.language === selectedLanguage);
        return {
          text: optionText?.text || "",
          id: optionText?.id || null,
          optionId: option.id
        };
      });

      form.setValue("optionsText", {
        ...form.getValues("optionsText"),
        [selectedLanguage]: optionsTexts
      });
    }
  }, [selectedLanguage, questionToUpdateData, form]);

  const addOption = () => {
    append({
      text: "",
      id: null,
      optionId: null
    });
  };

  const getTextByLanguage = (texts, language) => {
    if (!texts) return "";
    
    // Si es un arreglo de objetos de texto (formato de API)
    if (Array.isArray(texts)) {
      const textObj = texts.find((text) => text.language === language);
      return textObj ? textObj.text : "";
    }
    
    // Si es un objeto con claves de idioma (formato interno)
    if (typeof texts === 'object' && texts[language]) {
      return texts[language].text || texts[language] || "";
    }
    
    // Si es una cadena directa
    if (typeof texts === 'string') {
      return texts;
    }
    
    return "";
  };

  const onSubmit = async (values) => {
    setLoading(true);
    
    // Submit handler
    
    // Verificar que haya un idioma seleccionado
    if (!selectedLanguage) {
      toast.error(t("forms.privacyPoicy.selectLanguage"));
      setLoading(false);
      return;
    }
    
    // Solo enviar el idioma seleccionado en este momento
    const updateQuestionToSend = updateQuestionPayload({
      values,
      questionToUpdateData,
      selectedLanguage,
    });

    // Prepared payload for updateQuestion

    try {
      const resultado = await UpdatetQuestion(
        questionToUpdateData.id,
        updateQuestionToSend,
        formType,
      );
      toast.success(t("alertDialog.toast.questionUpdatedSuccess"));
      closeModal();
    } catch (error) {
      console.error("Error al actualizar la pregunta:", error);
      console.error("TRACE SUBMIT - Detalle completo del error:", JSON.stringify(error, Object.getOwnPropertyNames(error)));
      toast.error(t("alertDialog.toast.questionUpdatedError"));
    } finally {
      setLoading(false);
    }
  };

  const questions = allQuestions;

  const legalTextOptions = buildLegalTextOptions(
    legalTexts,
    form.watch("legalTextKey"),
    i18n.language,
  );

  return (
    <section className="w-full flex justify-center">
      <Card className="p-6 w-full text-black bg-slate-50">
        <Form {...form}>
          <form onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit(onSubmit)(e);
          }}>
            <FormField
              control={form.control}
              name="language"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>{t("forms.privacyPoicy.language")}</FormLabel>
                  <Select
                    onValueChange={(value) => {
                      setSelectedLanguage(value);
                    }}
                    value={selectedLanguage}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar idioma" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {questionLanguages.map((language) => (
                        <SelectItem key={language.id} value={language.code}>
                          {language.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {selectedLanguage && (
              <>
                <FormField
                  control={form.control}
                  name={`texts.${selectedLanguage}.text`}
                  render={({ field }) => (
                    <FormItem className="mb-4">
                      <FormLabel>
                        {t("forms.questions.question")} (
                        {selectedLanguage.toUpperCase()})
                      </FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`descriptions.${selectedLanguage}.text`}
                  render={({ field }) => (
                    <FormItem className="mb-4">
                      <FormLabel>
                        {t("forms.questions.description")} (
                        {selectedLanguage.toUpperCase()})
                      </FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`placeholders.${selectedLanguage}.text`}
                  render={({ field }) => (
                    <FormItem className="mb-4">
                      <FormLabel>
                        {t("forms.questions.placeholder")} (
                        {selectedLanguage.toUpperCase()})
                      </FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            <FormField
              control={form.control}
              name="active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-2 space-y-0 mb-4">
                  <FormControl>
                    <Checkbox
                      checked={normalizeBooleanField(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(normalizeBooleanField(checked))
                      }
                      id="active"
                    />
                  </FormControl>
                  <FormLabel
                    htmlFor="active"
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    {t("forms.questions.activate")}
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="required"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-2 space-y-0 mb-4">
                  <FormControl>
                    <Checkbox
                      checked={normalizeBooleanField(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(normalizeBooleanField(checked))
                      }
                      id="required"
                    />
                  </FormControl>
                  <FormLabel
                    htmlFor="required"
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    {t("forms.questions.required")}
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="anonymizable"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-2 space-y-0 mb-4">
                  <FormControl>
                    <Checkbox
                      checked={normalizeBooleanField(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(normalizeBooleanField(checked))
                      }
                      id="anonymizable"
                    />
                  </FormControl>
                  <FormLabel
                    htmlFor="anonymizable"
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    {t("forms.questions.anonymizable")}
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="screen"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>{t("forms.questions.position")}</FormLabel>
                  <Select
                    onValueChange={(value) =>
                      field.onChange(value === "null" ? null : Number(value))
                    }
                    value={
                      field.value === null ? "null" : field.value.toString()
                    }
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select screen" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="null">Ninguna</SelectItem>
                      <SelectItem value="1">
                        {t("forms.questions.screen")} 1
                      </SelectItem>
                      <SelectItem value="2">
                        {t("forms.questions.screen")} 2
                      </SelectItem>
                      <SelectItem value="3">
                        {t("forms.questions.screen")} 3
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>{t("forms.questions.questionType")}</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona tipo de pregunta" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="select">Select</SelectItem>
                      <SelectItem value="radio">Radio</SelectItem>
                      <SelectItem value="input">Input</SelectItem>
                      <SelectItem value="textarea">TextArea</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="terms">Términos</SelectItem>
                      <SelectItem value="file">Fichero</SelectItem>
                      <SelectItem value="password">Contraseña</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {form.watch("type") === "terms" && (
              <FormField
                control={form.control}
                name="legalTextKey"
                render={({ field }) => (
                  <FormItem className="mb-4">
                    <FormLabel>
                      {t("forms.questions.legalTextKey")}
                    </FormLabel>
                    <Select
                      value={field.value || "Terms"}
                      onValueChange={field.onChange}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Terms" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {legalTextOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      {t("forms.questions.legalTextKeyHelp")}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {(form.watch("type") === "select" ||
              form.watch("type") === "radio") && (
              <div className="space-y-4">
                <div className="flex justify-between items-center mt-2">
                  <FormLabel>{t("forms.questions.options")}</FormLabel>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addOption}
                  >
                    {t("forms.questions.addOption")}
                  </Button>
                </div>

                {fields.map((field, index) => {
                  // Obtener el placeholder de referencia directamente del field
                  const currentOption = form.getValues(`optionsText.${selectedLanguage}.${index}`);
                  let referenceText = currentOption?.text || "";
                  
                  // Si no hay texto en el idioma actual, buscar en el almacén de textos de referencia
                  if (!referenceText && currentOption?.optionId) {
                    // Try to get reference text for this optionId from stored texts
                    const optionTexts = optionsReferenceTexts[currentOption.optionId];
                    if (optionTexts) {
                      if (optionTexts["es"]) {
                        referenceText = optionTexts["es"];
                      } else if (optionTexts["en"]) {
                        referenceText = optionTexts["en"];
                      } else {
                        const availableLanguages = Object.keys(optionTexts);
                        if (availableLanguages.length > 0) {
                          referenceText = optionTexts[availableLanguages[0]];
                        }
                      }
                    }
                  }
                  
                  const placeholderText = referenceText ? 
                    referenceText : 
                    `${selectedLanguage.toUpperCase()} ${t("forms.questions.option")}`;
                  
                  // Depuración: Verificar si tenemos los textos de referencia para esta opción
                  
                  
                  // Depuración: Verificar el texto que estamos usando para el placeholder
                  
                  
                  return (
                    <div key={field.id} className="flex space-x-2">
                      <FormField
                        control={form.control}
                        name={`optionsText.${selectedLanguage}.${index}.text`}
                        render={({ field }) => (
                          <FormItem className="flex-1 mb-4">
                            <FormControl>
                              <Input
                                {...field}
                                placeholder={placeholderText}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => remove(index)}
                      >
                        {t("forms.questions.remove")}
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
            <FormField
                control={form.control}
                name="related_question"
                render={({ field }) => {
                  const candidates = (questions || []).map((q) => ({
                    id: q?.id,
                    type: String(q?.type || "").toLowerCase(),
                    texts: q?.texts,
                    alias: q?.alias,
                  })).filter((q) => q.type === "radio" || q.type === "select");
                  return (
                      <FormItem className="mb-4">
                        <FormLabel>{t("forms.questions.relatedQuestion")}</FormLabel>
                         <Select
                             onValueChange={(value) => {
                              if (value === "__none__") {
                                field.onChange(null);
                                setSelectedQuestion(null);
                                form.setValue("depends_id", null);
                                return;
                              }
                              const selectedQ = (questions || []).find((question) => question.id === Number(value));
                              field.onChange(Number(value));
                              setSelectedQuestion(selectedQ);
                             }}
                             value={field.value ? field.value.toString() : ""}
                         >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecciona la pregunta relacionada" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="__none__">— None —</SelectItem>
                            {candidates.map((question) => {
                                return (
                                    <SelectItem
                                        key={question.id}
                                        value={question.id.toString()}
                                    >
                                      {getTextByLanguage(question.texts, selectedLanguage) || question.alias}
                                    </SelectItem>
                                );
                            })}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                  );
                }}
            />


            {selectedQuestion && Array.isArray(selectedQuestion.options) && selectedQuestion.options.length > 0 && (
                <FormField
                    control={form.control}
                    name="depends_id"
                    render={({ field }) => (
                        <FormItem className="mb-4">
                          <FormLabel>{t("forms.questions.optionRelated")}</FormLabel>
                          <Select
                              onValueChange={(value) => {
                                field.onChange(Number(value));
                              }}
                              value={field.value ? field.value.toString() : ""}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Selecciona la opción relacionada" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {selectedQuestion.options.map((option) => (
                                  <SelectItem
                                      key={option.id}
                                      value={option.id.toString()}
                                  >
                                    {getTextByLanguage(option.text, selectedLanguage) || "no tiene opciones"}
                                  </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                    )}
                />
            )}


            <Button
              type="submit"
              className="mt-4 bg-primary hover:bg-primary-600"
            >
              {loading ? (
                <Loader style={"buttonLoader"} />
              ) : (
                t("buttonTexts.saveChanges")
              )}
            </Button>
          </form>
        </Form>
      </Card>
    </section>
  );
};

export default UpdateQuestion;
